import { Injectable } from '@angular/core';
import { Config, HttpMethod } from '@mitel/cloudlink-sdk';
import { environment } from '../../../environments/environment';
import { asyncJobHttpMethod } from '../constants/constants';
import { AsyncJobsService } from './async-jobs.service';
import { AppInjector } from './../../app-injector';
import { Location } from './../interfaces/collections';
import LRUCache from 'lru-cache';


@Injectable({
  providedIn: 'root'
})
export class MiVBProvisioningService {
  logContext = 'MiVBProvisioningService';
  private miVBProvisioningSvc: any;
  private asyncJobsService: AsyncJobsService;
  private userServiceCache = new LRUCache<string, Promise<any>>({
		ttl: 5 * 60 * 1000,  // ms (cache item expires in 5 minutes)
		max: 100             // and, oldest item in cache is bumped if more than this items in cache
		// https://github.com/isaacs/node-lru-cache#readme
	}); // cache by pbxUserId

  // store locations by networkElement
  private locationsCache = new LRUCache<string, any>({
    ttl: 60 * 10 * 1000,  // ms (cache item expires in 10 min)
    max: 10          // and, oldest item in cache is bumped if more than this items in cache
    // https://github.com/isaacs/node-lru-cache#readme
  }); // cache by networkElement ID

  // store networkElements by accountId
  private networkElementsCache = new LRUCache<string, any>({
    ttl: 60 * 10 * 1000,  // ms (cache item expires in 10 min)
    max: 10          // and, oldest item in cache is bumped if more than this items in cache
    // https://github.com/isaacs/node-lru-cache#readme
  }); // cache by account ID

  constructor() {
    this.miVBProvisioningSvc = {
      rootUrl: environment.mivbProvisioningUrl,
      _http: <HttpMethod>Config.httpService,

      async getMiVBUser(pbxUserId: string): Promise<any> {
        let url = `${this.rootUrl}/users/${pbxUserId}`;
        return await this._http.get({ url })
          .then(response => response.body);
      },

      async getMiVBUserLiveSync(pbxUserId: string): Promise<any> {
        let url = `${this.rootUrl}/users/${pbxUserId}?live=true`;
        return await this._http.get({ url })
          .then(response => response.body);
      },

      async getMiVBUserServices(pbxUserId: string): Promise<any> {
        let url = `${this.rootUrl}/users/${pbxUserId}/services`;
        return await this._http.get({ url })
          .then(response => response.body);
      },

      async deleteMiVBUser(pbxUserId: string, body?:any): Promise<any> {
        let url = `${this.rootUrl}/users/${pbxUserId}`;
        if(body){
          return await this._http.delete({ url, body: body })
          .then(response => response.body);
        }
        return await this._http.delete({ url })
          .then(response => response.body);
      },

      async putMiVBUser(pbxUserId: string, mivbConfiguration: any): Promise<any> {
        let url = `${this.rootUrl}/users/${pbxUserId}`;
        return await this._http.put({url, body: mivbConfiguration })
          .then((response: any) => response.body);
      },

      async getMiVBNetworkElements(): Promise<any> {
        let url = `${this.rootUrl}/network-elements`;
        return await this._http.get({ url })
          .then((response: any) => response.body);
      },

      async getMiVBUserDeploymentEntries(pbxUserId: string): Promise<any> {
        let url = `${this.rootUrl}/users/${pbxUserId}/deployments`;
        return await this._http.get({ url })
          .then((response: any) => response.body);
      },

      async getAsyncJobs(top: string, skipToken?: string): Promise<any> {
        let url = '';
        if (skipToken) {
          // rootUrl: 'https://mivb.provisioning.dev.api.mitel.io/2017-09-01'
          // skipToken: starts with /2017-09-01
          let lastSlash = this.rootUrl.lastIndexOf('/');
          url = this.rootUrl.substring(0, lastSlash) + skipToken;
        }
        else
          url = `${this.rootUrl}/async-jobs?$top=${top}`;

        return await this._http.get({ url })
          .then((response: any) => response.body);
      },

      async getAsyncJobById(asyncJobId: string, query?: string): Promise<any> {
        let url = `${this.rootUrl}/async-jobs/${asyncJobId}${query}`;
        return await this._http.get({ url })
          .then((response: any) => response.body);
      },

      async putAsyncJobById(asyncJobId: string, body: any): Promise<any> {
        let url = `${this.rootUrl}/async-jobs/${asyncJobId}`;
        return await this._http.put({ url, body: body })
          .then((response: any) => response.body);
      },
      
      async getLocations(networkElementId: string): Promise<any[]> {
        let url = `${this.rootUrl}/network-elements/${networkElementId}/locations`;
        return await this._http.get({ url }).then((response: any) => response.body);
      }
    }
  }

  async initializeServices(){
    if (!this.asyncJobsService)
      this.asyncJobsService = AppInjector.injector.get(AsyncJobsService);
  }

  public getMiVBUser(pbxUserId: string): Promise<any> {
    return this.miVBProvisioningSvc.getMiVBUser(pbxUserId);
  }

  public getMiVBUserLiveSync(pbxUserId: string): Promise<any> {
    return this.miVBProvisioningSvc.getMiVBUserLiveSync(pbxUserId);
  }

  public removeUserFromCache(pbxUserId: string): boolean {
    if (pbxUserId && this.userServiceCache.has(pbxUserId)) {
      return this.userServiceCache.delete(pbxUserId);
    }
    return false;
  }

  public getMiVBUserServices(pbxUserId: string): Promise<any> {
    if (!this.userServiceCache.has(pbxUserId)) {
      const promise = this.miVBProvisioningSvc.getMiVBUserServices(pbxUserId)
        .then(
          result => result,
          (error: any) => {
            this.userServiceCache.delete(pbxUserId);  // remove rejected Promise from cache
            throw error;
          }
        );
      this.userServiceCache.set(pbxUserId, promise);
    }
    return this.userServiceCache.get(pbxUserId);
  }

  public deleteMiVBUser(pbxUserId: string): Promise<any> {
    //remove user from the cache - although the pbxUserId will be removed
    this.removeUserFromCache(pbxUserId);
    return this.miVBProvisioningSvc.deleteMiVBUser(pbxUserId);
  }

  public deleteUserPhoneServices(pbxUserId: string): Promise<any> {
    let body = { phones: [] };
    //remove user from the cache if the user's phones are deleted
    this.removeUserFromCache(pbxUserId);
    return this.miVBProvisioningSvc.putMiVBUser(pbxUserId, body);
  }

  public updateMiVBUser(pbxUserId: string, body: any): Promise<any> {
    return this.miVBProvisioningSvc.putMiVBUser(pbxUserId, body);
  }

  public async getMiVBNetworkElements(accountId: string): Promise<any| undefined> {
    if (!accountId) {
      return Promise.reject('missing accountId');
    }
    if (!this.networkElementsCache.has(accountId)) {
      try {
        const response = await this.miVBProvisioningSvc.getMiVBNetworkElements();
        this.networkElementsCache.set(accountId, response);
      }
      catch (error) {
        if (error.statusCode == 404) { // message :  "Premise server not found"
          this.networkElementsCache.set(accountId, []);
          console.log(`${this.logContext}.getMiVBNetworkElements - NetworkElements not found `, error);
        } else {
          this.networkElementsCache.delete(accountId);
          console.error(`${this.logContext}.getMiVBNetworkElements - Error determining networkElements`, error);
          throw error;
        }
      }
    }
    return this.networkElementsCache.get(accountId);  
  }

  public async getMiVBUserDeploymentEntries(pbxUserId: string): Promise<any> {
    try {
      let response = await this.miVBProvisioningSvc.getMiVBUserDeploymentEntries(pbxUserId);
      if(response && response.count && response.count > 0 && response._embedded && response._embedded.items){
        let deployments = response._embedded.items;
        console.log(`deployment records for pbxUserId: ${pbxUserId}: `, deployments);
        return Promise.resolve(deployments);
      }
      else{
        return Promise.reject(null);
      }
      
    } catch (error) {
      if(error && error.statusCode === 404){
        return Promise.resolve([]);
      }
      else{
        console.warn(`failed to get deployment records for pbxUserId: ${pbxUserId}: `, error );
        return Promise.reject(null);
      }
    }
  }

  public getAsyncJobs(top: number, skipToken?: string): Promise<any> {
    return this.miVBProvisioningSvc.getAsyncJobs(top, skipToken)
      .catch( error => {
        if ( error.statusCode == 404 )
          return Promise.resolve([]);
        else
          return Promise.reject(error);
      });
  }

  public getAsyncJobById(asyncJobId: string, query?: string): Promise<any> {
    return this.miVBProvisioningSvc.getAsyncJobById(asyncJobId, query);
  }

  public putAsyncJobById(asyncJobId: string, body: any): Promise<any> {
    return this.miVBProvisioningSvc.putAsyncJobById(asyncJobId, body);
  }

  public async updateMiVBUserAsyncWithPolling(accountId: string, userId: string, pbxUserId: string, body: any): Promise<any> {
    body["async"] = true;
    try {
      let asyncJob = await this.miVBProvisioningSvc.putMiVBUser(pbxUserId, body);
      if(asyncJob && asyncJob.jobId){
        this.initializeServices();
        let jobLink = `/accounts/${accountId}/users/${userId}/product-mivb?asyncJobId=${asyncJob.jobId}`;
        await this.asyncJobsService.addMiVBAsyncJobAndPollById(asyncJob.jobId, userId, asyncJobHttpMethod.PUT, jobLink, 300000); //wait 5min max
        console.log(`Success: Async-update of mivb userId: ${userId}`);
        return Promise.resolve();
      }
      else{
        console.log(`Failed: Async-update of mivb userId: ${userId}, error: no asyncJob or jobId`);
        return Promise.reject('no asyncJob or jobId');
      } 
    } catch (error) {
      console.log(`Failed: Async-update of mivb userId: ${userId}, error: other`);
      return Promise.reject(error);
    }
  }

  public async deleteMiVBUserAsyncWithPolling(accountId: string, userId: string, pbxUserId: string): Promise<any> {
    let body = { async: true }; //no body normally for delete request
    try {
      let asyncJob = await this.miVBProvisioningSvc.deleteMiVBUser(pbxUserId, body);
      if(asyncJob && asyncJob.jobId){
        this.initializeServices();
        /* 
        - typically after this method is called, the user is also deleted from cloudlink
        - so go to the users list page if the async job is clicked
        */
        let jobLink = `/accounts/${accountId}/users`;
        await this.asyncJobsService.addMiVBAsyncJobAndPollById(asyncJob.jobId, userId, asyncJobHttpMethod.DELETE, jobLink, 300000); //wait 5min max
        console.log(`Success: Async-delete of mivb userId: ${userId}`);
        return Promise.resolve();
      }
      else{
        console.log(`Failed: Async-delete of mivb userId: ${userId}, error: no asyncJob or jobId`);
        return Promise.reject('no asyncJob or jobId');
      } 
    } catch (error) {
      console.log(`Failed: Async-delete of mivb userId: ${userId}, error: other`);
      return Promise.reject(error);
    }
  }

  async getLocations(networkElementId: string): Promise<Location[] | undefined> {
    if (!networkElementId) {
      return Promise.reject('missing networkElementId');
    }
    if (!this.locationsCache.has(networkElementId)) {
      try {
        const response = await this.miVBProvisioningSvc.getLocations(networkElementId);
        const locations = response && ('locations' in response) ? response.locations : undefined;
        if (locations) {
          this.locationsCache.set(networkElementId, locations);
        }
      }
      catch (error) {
        this.locationsCache.delete(networkElementId);
        console.error(`${this.logContext}.getLocations - Error determining locations`, error);
        throw error;
      }
    }
    return this.locationsCache.get(networkElementId);   
  }
}
